'use client';
import { useRef, useState } from 'react';
import { SplitMediaBlock as ISplitMediaBlock } from '@/components/types';
import clsx from 'clsx';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import NavigationArrow from '@/assets/nav_arrow.svg';
import ContentContainer from '../layout/ContentContainer';

export interface Theme {
  button_bg: string;
  pre_title: string;
  title: string;
  content: string;
  pagination_dot: string;
  link_bg: string;
  link_text: string;
}

const themes: Record<ISplitMediaBlock['theme'], Theme> = {
  indigo: {
    button_bg: 'bg-teal-700',
    pre_title: 'text-teal-700',
    title: 'text-teal-700',
    content: 'text-white',
    pagination_dot: 'bg-white',
    link_bg: 'bg-teal-700',
    link_text: 'text-white',
  },
  gray: {
    button_bg: 'bg-teal-700',
    pre_title: 'text-graphite-700',
    title: 'text-teal-700',
    content: 'text-white',
    pagination_dot: 'bg-white',
    link_bg: 'bg-teal-700',
    link_text: 'text-white',
  },
  white: {
    button_bg: 'bg-qf-purple',
    pre_title: 'text-indigo-800',
    title: 'text-qf-black',
    content: 'text-graphite-700',
    pagination_dot: 'bg-qf-purple',
    link_bg: 'bg-teal-700',
    link_text: 'text-white',
  },
};

// Grid column proportions for text:image at lg+ breakpoints
const gridProportions: Record<string, string> = {
  small: 'lg:grid-cols-[50%_50%]',
  medium: 'lg:grid-cols-[45%_55%]',
  large: 'lg:grid-cols-[40%_60%]',
  xl: 'lg:grid-cols-[35%_65%]',
};

const backgroundColour = {
  white: 'bg-white',
  indigo: 'bg-indigo-800',
  gray: 'bg-gray-700',
  purple: 'bg-qf-purple',
  teal: 'bg-teal-700',
  transparent: 'bg-transparent',
};

const SwiperCarousel = ({ block }: { block: ISplitMediaBlock }) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const theme = themes[block.theme] ?? themes.white;

  return block.images ? (
    <div>
      <div className="flex justify-center">
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          slidesPerView={1}
          a11y={{ enabled: true }}
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          onSlideChange={swiper => setActiveIndex(swiper.activeIndex)}
          className="mySwiper w-full"
        >
          {block.images.map((image, idx) => (
            <SwiperSlide key={`media-block-swiper-slide-${idx}`}>
              <div className="relative aspect-video">
                <Image
                  src={image.url || ''}
                  alt={image.alt || ''}
                  fill
                  className="object-contain"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Custom pagination and arrows below, only if more than one image */}
      {block.images.length > 1 && (
        <div className="mt-6 flex flex-col items-center gap-4 lg:flex-row lg:justify-center">
          <div className={clsx('flex gap-2', theme.title)}>
            {/* Pagination dots - always visible - on mobile put em on top of the arrows*/}
            {block.images?.map((_, idx) => (
              <button
                key={`media-block-pagination-dot-${idx}`}
                className={clsx(
                  'h-3 w-3 rounded-full transition-all hover:cursor-pointer',
                  activeIndex === idx ? theme.pagination_dot : 'bg-gray-200/30',
                )}
                onClick={() => swiperRef.current?.slideTo(idx)}
              />
            ))}
          </div>
          {/* Arrows - always visible */}
          <div className="flex gap-1 rounded-full border border-gray-200">
            <button
              className={clsx(
                'm-1 flex h-10 w-10 items-center justify-center rounded-full hover:cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50',
                'bg-qf-purple/10',
              )}
              onClick={() => swiperRef.current?.slidePrev()}
              disabled={activeIndex === 0}
            >
              <NavigationArrow className={clsx(theme.pre_title)} />
            </button>
            <button
              className={clsx(
                'm-1 flex h-10 w-10 items-center justify-center rounded-full hover:cursor-pointer hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50',
                'bg-qf-purple/10',
              )}
              onClick={() => swiperRef.current?.slideNext()}
              disabled={activeIndex === (block.images?.length || 0) - 1}
            >
              <NavigationArrow
                className={clsx('rotate-180', theme.pre_title)}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;
};

export default function SplitMediaBlock({
  block,
}: {
  block: ISplitMediaBlock;
}) {
  const theme = themes[block.theme] ?? themes.white;
  const bgColour =
    backgroundColour[block.background_colour] ?? backgroundColour.white;
  const content = block.contents?.[0];
  const imagePosition = block.image_position ?? 'right';

  return content ? (
    <ContentContainer>
      <div
        className={clsx(
          'grid w-full grid-cols-1 items-center justify-center gap-6 py-8 sm:gap-10 2xl:gap-24',
          gridProportions[block.text_width as keyof typeof gridProportions] ||
            gridProportions.small,
          bgColour,
        )}
      >
        <div
          className={clsx(
            'flex w-full flex-col justify-center',
            imagePosition === 'left' ? 'lg:order-2' : 'lg:order-1',
          )}
        >
          {content.pre_title && (
            <div
              className={clsx(
                'font-inter mb-4 text-[14px] leading-[24px] font-medium tracking-[10%] uppercase',
                theme.pre_title,
              )}
              dangerouslySetInnerHTML={{
                __html: content.pre_title,
              }}
            />
          )}
          {content.title && (
            <div
              className={clsx(
                'font-source-serif-pro mb-4 text-3xl leading-[125%] tracking-[-2%]',
                theme.title,
              )}
              dangerouslySetInnerHTML={{ __html: content.title }}
            />
          )}
          {content.body && (
            <div
              className={clsx(
                'prose-content font-inter mb-6 text-[17px] leading-[26px] font-normal tracking-[0%]',
                theme.content,
              )}
              dangerouslySetInnerHTML={{
                __html: content.body,
              }}
            />
          )}
          {block.button?.text && block.button?.url && (
            <a
              href={block.button.url}
              className={clsx(
                'inline-flex w-fit items-center gap-2 px-6 py-3 font-medium transition-opacity hover:opacity-90',
                theme.link_bg,
                theme.link_text,
              )}
            >
              {block.button.text}
            </a>
          )}
        </div>
        <div
          className={clsx(
            'w-full',
            imagePosition === 'left' ? 'lg:order-1' : 'lg:order-2',
          )}
        >
          <SwiperCarousel block={block} />
        </div>
      </div>
    </ContentContainer>
  ) : null;
}
